export const getConnectedFileExtension = (fileExt: string) => {
  if (fileExt?.includes("google_drive")) {
    return "Google Drive";
  }
  if (fileExt?.includes("google_chat")) {
    return "Google Chat";
  }
  if (fileExt?.includes("teamwork_cloud")) {
    return "Teamwork Cloud";
  }
  if (fileExt?.includes("windchill")) {
    return "Windchill";
  }
  if (fileExt?.includes("3d_experience")) {
    return "3DEXPERIENCE";
  }
  if (fileExt?.includes("microsoft_365")) {
    return "Microsoft 365";
  }
  return fileExt;
};

export const getFileNameAndExtension = (fileName: string | null | undefined) => {
  if (!fileName) {
    return null;
  }
  const trimInd = fileName.lastIndexOf(".");
  if (trimInd === -1) {
    return { extension: "", name: fileName };
  }
  return {
    extension: fileName.substring(trimInd + 1),
    name: fileName.substring(0, trimInd),
  };
};

// What a download of a revision is called: the revision states its own name and extension, so
// neither is recovered by splitting a display name. That split is what a display name cannot
// survive -- "Car Speed v1.2" parses to a "2" extension, which then hides the media-type fallback
// a connected resource depends on for its suffix.
export const revisionDownloadName = (revision: {
  displayName?: string | null;
  name?: string | null;
  extension?: string | null;
  mime?: string | null;
}): { name: string; extension: string } => {
  const mimeExtension = downloadExtensionForMime(revision.mime);
  // Normalized first: the backend states extensions dotted or dotless and in any casing, and a
  // dotted `.istari_..._metadata_element` has to reach the connector branch below like any other.
  const statedExtension = normalizeFileExtension(revision.extension) || "";

  // A connector states what it minted here -- `istari_teamwork_cloud_metadata_element` for a TWC
  // element -- which says what the resource is, not what the bytes are, and saved under it the file
  // opens in nothing. The `istari` prefix marks every one of these, whatever the connector calls
  // the rest, so a minted value is never the suffix: the media type answers instead, and when it
  // explains nothing the download carries no suffix at all rather than a misleading one.
  const mintedExtension = statedExtension.startsWith("istari");
  if (statedExtension || mimeExtension) {
    return {
      // Only a suffix that matches one of these comes off: splitting on the last dot would take
      // the ".2" from "Car Speed v1.2", which is part of the name.
      name: pick(revision.displayName, revision.name, statedExtension, mimeExtension),
      extension: (mintedExtension ? mimeExtension : statedExtension || mimeExtension) ?? "",
    };
  }

  // Nothing stated and nothing derivable: splitting the file name is the last thing left.
  const parsedFileName = getFileNameAndExtension(revision.name);
  return {
    name: stripExtensionFromDisplayName(revision.displayName, parsedFileName?.extension) || parsedFileName?.name || "",
    extension: parsedFileName?.extension || "",
  };
};

// The first name there is, without any of the suffixes the caller might append back. Both are
// stripped, not just the one being returned: a name can already carry the media-type suffix
// ("data.json") while the stated extension is a minted one, and appending json to that would
// double it.
const pick = (
  displayName: string | null | undefined,
  name: string | null | undefined,
  ...extensions: (string | undefined)[]
) =>
  [displayName, name]
    .map((value) =>
      extensions.reduce<string | null | undefined>(
        (current, extension) => stripExtensionFromDisplayName(current, extension),
        value,
      ),
    )
    .find(Boolean) || "";

// A filename extension for content whose resource states none. Connected content is the case that
// needs it: a model element is not a file at its source, so the registry reports no extension --
// but the bytes we hand the browser are JSON-LD, and a download called `Parent` with no suffix
// opens in nothing. Derived from the media type, which is the only honest statement of what the
// bytes are. Unknown types get nothing rather than a guess.
export const downloadExtensionForMime = (mime: string | null | undefined): string | undefined => {
  const subtype = mime?.split(";")[0]?.trim().toLowerCase().split("/")[1];
  if (!subtype) return undefined;
  // `application/ld+json` (a TWC element), `application/vnd.istari.container-manifest+json`
  // (a container's manifest), plain `application/json`.
  if (subtype === "json" || subtype.endsWith("+json")) return "json";
  return undefined;
};

// Backend sends file extensions inconsistently: dotless ("glb") or dotted (".glb"), and any casing.
// Callers compare against a bare, lowercase form, so coerce to that and treat blank input as absent.
export const normalizeFileExtension = (extension: string | null | undefined): string | undefined => {
  const trimmed = extension?.trim();
  if (!trimmed) return undefined;
  const withoutDot = trimmed.startsWith(".") ? trimmed.slice(1) : trimmed;
  return withoutDot ? withoutDot.toLowerCase() : undefined;
};

// Strips the file extension from a display name if it ends with that extension.
// Handles extensions with or without a leading dot (e.g., "xlsx" or ".xlsx").
export const stripExtensionFromDisplayName = (
  displayName: string | null | undefined,
  extension: string | null | undefined,
): string | undefined => {
  if (!displayName) return undefined;
  if (!extension) return displayName;
  const normalizedExt = extension.startsWith(".") ? extension.slice(1) : extension;
  if (normalizedExt && displayName.toLowerCase().endsWith(`.${normalizedExt.toLowerCase()}`)) {
    return displayName.slice(0, -(normalizedExt.length + 1));
  }
  return displayName;
};

// The extension as a person should read it. A connector mints its own -- an
// `istari_teamwork_cloud_metadata_element` for a TWC element -- to say what the resource is, not
// what the bytes are, so it belongs to no name: a connected resource shows as its name alone.
export const displayExtension = (extension: string | null | undefined): string | undefined =>
  checkIsLinkedModel(normalizeFileExtension(extension)) ? undefined : (extension ?? undefined);

export const checkIsLinkedModel = (extension: string | undefined): boolean => {
  if (!extension) {
    return false;
  }
  return extension.startsWith("istari") && extension.includes("metadata");
};

export const getLiteralModelName = (extension: string | undefined): string | undefined => {
  switch (extension) {
    case "catpart":
    case "catproduct":
      return "Catia";
    case "mdzip":
      return "Cameo";
    case "xlsx":
      return "Excel";
    default:
      return getConnectedFileExtension(extension!);
  }
};

export const getExtractableFileType = (extension: string): string => {
  switch (extension) {
    case "catpart":
    case "catproduct":
      return "catia";
    case "docx":
      return "word";
    case "mdzip":
      return "cameo";
    case "xlsx":
    case "csv":
      return "excel";
    default:
      return extension;
  }
};

export const getAppHostFromExt = (ext: string | null | undefined) =>
  ext ? ext.match(/istari_(.*)_metadata.*/)?.[1] : undefined;

export function isZipFile(extension?: string): boolean {
  return !!extension && extension.toLowerCase() === "zip";
}
